/**
 * Client-side cart state. Persists to localStorage so refresh/reload keep items.
 * The shared drawer confirms compound subscriptions and one-time supplement
 * orders through their authenticated backend purchase endpoints.
 */

import { useSyncExternalStore } from "react";
import { brand } from "@brand";
import type { ShopDoseOffer } from "@/types/api";

export type PlanFrequency =
  | "monthly"
  | "quarterly"
  | "sixMonth"
  | "twelveMonth";

/** First-cycle display price (cents) per offerable term. A term is only
 *  offerable when its price is a positive number. */
export interface PlanPrices {
  monthly?: number | null;
  quarterly?: number | null;
  sixMonth?: number | null;
  twelveMonth?: number | null;
}

export interface CartItem {
  id: string; // product id
  slug: string;
  title: string;
  subtitle?: string;
  price_cents: number;
  image_url: string | null;
  quantity: number;
  origin: 'product' | 'protocol' | 'supplement' | 'lab';
  lab_purchase_type?: import('@/types/labs').LabPurchaseType;
  lab_interval_months?: import('@/types/labs').LabInterval;
  lab_subscription_price_cents?: number;
  lab_one_time_price_cents?: number;
  // A one-time course: one charge at price_cents, bought through the course
  // endpoint. It never carries a frequency, plan prices or renewal prices.
  purchase_type?: 'one_time_course';
  // Plan length. Older persisted carts predate this field — treat missing
  // as 'monthly' (the only term that existed then).
  frequency?: PlanFrequency;
  // Per-term prices so the cart drawer can reprice when the patient
  // changes plan length. Missing → item is monthly-only (no selector).
  plan_prices?: PlanPrices;
  // Ongoing renewal price per term (cents) — shown as "Renews at $X"
  // so the intro price is never mistaken for the recurring price.
  renewal_prices?: PlanPrices;
  dose_offer_id?: string;
  dose_offer_version?: string;
  // The treatment's default dose (e.g. NAD+ 1000 mg) is the plain product at
  // the same price; it is purchased without the dose-upgrade contract.
  dose_offer_default?: boolean;
  dose_contents?: string;
  // Stack attribution — set when the item was added via a Protocols-tab
  // stack card. Analytics-only.
  stack_slug?: string;
  // Supplement analytics snapshot, retained across cart reloads. The backend
  // resolves the product ID independently for pricing and fulfillment.
  supplement_category?: string;
  supplement_brand?: string;
  supplement_asin?: string;
  // Lifecycle marketing attribution — stamped when the item was added via a
  // campaign email deep link (?campaign=&message=&rec=). Metadata-only:
  // carried through to the purchase call so backend analytics can attribute
  // the conversion; never affects pricing or fulfillment. Flat fields match
  // the staging marketplace convention.
  lifecycle_campaign_key?: string;
  lifecycle_message_key?: string;
  lifecycle_recommendation_id?: string;
}

interface CartState {
  items: CartItem[];
  ownerId?: string;
  appliedSuggestedCartIds?: string[];
}

// "system.portal.cart.v1" in the house build (brand.storageKeyPrefix).
const STORAGE_KEY = `${brand.storageKeyPrefix}portal.cart.v1`;

/** Compound subscriptions remain quantity one; retail supplements allow 1–3.
 * Normalize persisted data and collapse duplicate ids before showing totals. */
export function normalizeCartItems(items: CartItem[]): CartItem[] {
  const byId = new Map<string, CartItem>();
  for (const item of items) {
    if (!item?.id || byId.has(item.id)) continue;
    byId.set(item.id, { ...item, quantity: cartQuantity(item, item.quantity) });
  }
  return [...byId.values()];
}

function load(): CartState {
  if (typeof window === "undefined") return { items: [] };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw) as CartState;
    return {
      ownerId: typeof parsed.ownerId === 'string' ? parsed.ownerId : undefined,
      appliedSuggestedCartIds: Array.isArray(parsed.appliedSuggestedCartIds)
        ? parsed.appliedSuggestedCartIds.filter((id): id is string => typeof id === 'string')
        : [],
      items: normalizeCartItems(
        Array.isArray(parsed.items) ? parsed.items : [],
      ),
    };
  } catch {
    return { items: [] };
  }
}

let state: CartState = load();
const listeners = new Set<() => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    const stored = load();
    if (state.ownerId && stored.ownerId !== state.ownerId) return;
    state = stored;
    listeners.forEach((listener) => listener());
  });
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota or SSR */
  }
  listeners.forEach((l) => l());
}

function cartQuantity(item: Pick<CartItem, 'origin'>, quantity: number) {
  return item.origin === 'supplement'
    ? Math.max(
        1,
        Math.min(3, Number.isFinite(quantity) ? Math.floor(quantity) : 1),
      )
    : 1;
}

export const cart = {
  subscribe,
  getSnapshot,
  syncFromStorage() {
    if (typeof window !== 'undefined') {
      const stored = load();
      if (state.ownerId && stored.ownerId !== state.ownerId) return state.items;
      state = stored;
      listeners.forEach((listener) => listener());
    }
    return state.items;
  },
  setOwner(ownerId: string) {
    if (state.ownerId === ownerId) return;
    // Unowned legacy storage is not proof of this authenticated patient.
    state = { ownerId, items: [] };
    persist();
  },
  hasAppliedSuggestion(id: string) {
    return state.appliedSuggestedCartIds?.includes(id) ?? false;
  },
  markSuggestionApplied(id: string) {
    if (cart.hasAppliedSuggestion(id)) return;
    state = {
      ...state,
      appliedSuggestedCartIds: [...(state.appliedSuggestedCartIds ?? []), id],
    };
    persist();
  },
  add(item: Omit<CartItem, 'quantity'>, requestedQuantity = 1) {
    const existing = state.items.find((i) => i.id === item.id);
    if (existing) {
      if (
        !item.dose_offer_id ||
        (existing.dose_offer_id === item.dose_offer_id &&
          existing.dose_offer_version === item.dose_offer_version)
      )
        return false;
      const frequency = existing.frequency ?? "monthly";
      const price = item.plan_prices?.[frequency];
      const replacement =
        typeof price === "number" && price > 0
          ? { ...item, frequency, price_cents: price, quantity: 1 }
          : { ...item, quantity: 1 };
      state = {
        ...state,
        items: state.items.map((old) =>
          old.id === item.id ? replacement : old,
        ),
      };
      persist();
      return true;
    }
    state = {
      ...state,
      items: [
        ...state.items,
        { ...item, quantity: cartQuantity(item, requestedQuantity) },
      ],
    };
    persist();
    return true;
  },
  remove(id: string) {
    state = { ...state, items: state.items.filter((i) => i.id !== id) };
    persist();
  },
  setQuantity(id: string, quantity: number) {
    if (quantity <= 0) {
      cart.remove(id);
      return;
    }
    state = {
      ...state,
      items: state.items.map((i) =>
        i.id === id ? { ...i, quantity: cartQuantity(i, quantity) } : i,
      ),
    };
    persist();
  },
  updateSupplementOffer(id: string, priceCents: number) {
    if (!Number.isInteger(priceCents) || priceCents <= 0 || priceCents > 100_000) return;
    state = {
      ...state,
      items: state.items.map((item) =>
        item.id === id && item.origin === 'supplement'
          ? { ...item, price_cents: priceCents }
          : item,
      ),
    };
    persist();
  },
  /** Change an item's plan length. No-op unless the item carries a
   *  positive price for that term — price_cents follows the term so the
   *  subtotal and the purchase payload stay in lockstep. */
  setFrequency(id: string, frequency: PlanFrequency) {
    state = {
      ...state,
      items: state.items.map((i) => {
        if (i.id !== id || i.origin === 'supplement' || i.origin === 'lab' || isCourseItem(i)) return i;
        const price = i.plan_prices?.[frequency];
        if (typeof price !== "number" || price <= 0) return i;
        return { ...i, frequency, price_cents: price };
      }),
    };
    persist();
  },
  setLabOptions(id: string, purchaseType: import('@/types/labs').LabPurchaseType, interval: import('@/types/labs').LabInterval) {
    state = { ...state, items: state.items.map((item) => {
      if (item.id !== id || item.origin !== 'lab') return item;
      const price = purchaseType === 'subscription' ? item.lab_subscription_price_cents : item.lab_one_time_price_cents;
      return { ...item, lab_purchase_type: purchaseType, lab_interval_months: interval,
        price_cents: typeof price === 'number' && Number.isInteger(price) && price > 0 ? price : item.price_cents };
    }) };
    persist();
  },
  setLabPrices(id: string, subscriptionCents: number, oneTimeCents: number) {
    if (![subscriptionCents, oneTimeCents].every((price) => Number.isInteger(price) && price > 0 && price <= 100_000)) return;
    let changed = false;
    const items = state.items.map((item) => {
      if (item.id !== id || item.origin !== 'lab') return item;
      const price = item.lab_purchase_type === 'one_time' ? oneTimeCents : subscriptionCents;
      if (item.price_cents === price && item.lab_subscription_price_cents === subscriptionCents && item.lab_one_time_price_cents === oneTimeCents) return item;
      changed = true;
      return { ...item, price_cents: price, lab_subscription_price_cents: subscriptionCents, lab_one_time_price_cents: oneTimeCents };
    });
    if (!changed) return;
    state = { ...state, items };
    persist();
  },
  clear() {
    state = { ...state, items: [], appliedSuggestedCartIds: [] };
    persist();
  },
  subtotalCents(): number {
    return state.items.reduce((acc, i) => acc + i.price_cents * i.quantity, 0);
  },
};

/** Base cart item for a marketplace product — one place so every add
 *  surface (product card, detail modal, interests modal, stack card)
 *  carries the same plan-price data for the drawer's term selector. */
export function cartItemFromProduct(
  product: {
    id: string;
    slug: string;
    title: string;
    subtitle?: string | null;
    treatment_support?: string | null;
    price_cents?: number | null;
    quarterly_price_cents?: number | null;
    six_month_price_cents?: number | null;
    twelve_month_price_cents?: number | null;
    renewal_price_cents?: number | null;
    quarterly_renewal_price_cents?: number | null;
    six_month_renewal_price_cents?: number | null;
    twelve_month_renewal_price_cents?: number | null;
    dose_offers?: ShopDoseOffer[];
    hero_image_url?: string | null;
    purchase_mode?: 'subscription' | 'one_time_course';
  },
  selectedOffer?: ShopDoseOffer,
): Omit<CartItem, "quantity"> {
  if (product.purchase_mode === "one_time_course")
    return {
      id: product.id,
      slug: product.slug,
      title: product.title,
      subtitle: product.treatment_support ?? product.subtitle ?? undefined,
      price_cents: product.price_cents ?? 0,
      image_url: product.hero_image_url ?? null,
      origin: "product",
      purchase_type: "one_time_course",
    };
  const offer =
    selectedOffer ?? product.dose_offers?.find((option) => option.is_default);
  const item: Omit<CartItem, "quantity"> = {
    id: product.id,
    slug: product.slug,
    title: product.title,
    subtitle: product.treatment_support ?? product.subtitle ?? undefined,
    price_cents: product.price_cents ?? 0,
    image_url: product.hero_image_url ?? null,
    origin: "product",
    frequency: "monthly",
    plan_prices: {
      monthly: product.price_cents,
      quarterly: product.quarterly_price_cents,
      sixMonth: product.six_month_price_cents,
      twelveMonth: product.twelve_month_price_cents,
    },
    renewal_prices: {
      monthly: product.renewal_price_cents,
      quarterly: product.quarterly_renewal_price_cents,
      sixMonth: product.six_month_renewal_price_cents,
      twelveMonth: product.twelve_month_renewal_price_cents,
    },
  };
  return offer
    ? {
        ...item,
        title: offer.title,
        subtitle: offer.contents,
        price_cents: offer.plan_prices.monthly ?? 0,
        plan_prices: offer.plan_prices,
        renewal_prices: offer.renewal_prices,
        dose_offer_id: offer.id,
        dose_offer_version: offer.version,
        dose_offer_default: offer.is_default,
        dose_contents: offer.contents,
      }
    : item;
}

export function isCourseItem(item: Pick<CartItem, "purchase_type">): boolean {
  return item.purchase_type === "one_time_course";
}

/** "Renews at" cents for the item's current term — null when unknown or
 *  when the renewal equals the first-cycle price (nothing to disclose). */
export function renewalCentsFor(item: CartItem): number | null {
  const freq = item.frequency ?? "monthly";
  const renewal = item.renewal_prices?.[freq];
  if (typeof renewal !== "number" || renewal <= 0) return null;
  const first = item.plan_prices?.[freq] ?? item.price_cents;
  if (typeof first === "number" && renewal === first) return null;
  return renewal;
}

export const FREQUENCY_CYCLE_LABELS: Record<PlanFrequency, string> = {
  monthly: "/mo",
  quarterly: " every 3 months",
  sixMonth: " every 6 months",
  twelveMonth: " every 12 months",
};

export const FREQUENCY_LABELS: Record<PlanFrequency, string> = {
  monthly: "Monthly",
  quarterly: "3-month",
  sixMonth: "6-month",
  twelveMonth: "12-month",
};

/** Terms this item can actually be bought at, in display order. */
export function offerableFrequencies(item: CartItem): PlanFrequency[] {
  const pp = item.plan_prices;
  if (!pp) return ["monthly"];
  return (["monthly", "quarterly", "sixMonth", "twelveMonth"] as const).filter(
    (f) => typeof pp[f] === "number" && (pp[f] as number) > 0,
  );
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): CartState {
  return state;
}

export function useCart<T>(selector: (s: CartState) => T): T {
  return useSyncExternalStore(subscribe, () => selector(getSnapshot()));
}
